import React, { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, CheckCircle2, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { CTAButton } from "../components/CTAButton";
import { submitLead } from "../lib/submit";
import { PLANS } from "../data/plans";
import { CYCLES } from "../data/cycles";

const SEGMENT_KEYS = ["digital_nomad", "seasonal_worker", "trader_conference", "relocation", "family", "executive_hnw", "partner_agency"];
const TRAVELER_KEYS = [["individual", 1], ["couple", 2], ["family", 4], ["group", 6], ["entourage", 8]];
const NEED_KEYS = ["travel_assistance", "smartbag", "telemedicine", "connectivity", "risk_options", "monthly_payment", "pre_existing"];
const TOTAL = 6;

export default function Quote() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [assisted, setAssisted] = useState(false);
  const [countryInput, setCountryInput] = useState("");

  const [form, setForm] = useState({
    segment: "", currentCountry: "", nextDestination: "", countries: [],
    traveler: "", travelerCount: 0, needs: [],
    plan: params.get("plan") || "", cycle: params.get("cycle") || "",
    name: "", email: "", whatsapp: "", country_of_residence: "", start_date: "", notes: "",
    ack1: false, ack2: false,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleNeed = (v) => setForm((f) => ({ ...f, needs: f.needs.includes(v) ? f.needs.filter((x) => x !== v) : [...f.needs, v] }));

  const addCountry = () => {
    const c = countryInput.trim();
    if (c && !form.countries.includes(c)) set("countries", [...form.countries, c]);
    setCountryInput("");
  };

  const validateStep = () => {
    if (step === 1 && !form.segment) return t("quote.errSegment");
    if (step === 2 && !form.currentCountry) return t("quote.errCurrent");
    if (step === 3 && !form.traveler) return t("quote.errTraveler");
    if (step === 4 && form.needs.length === 0) return t("quote.errNeeds");
    if (step === 5) {
      if (!form.name) return t("quote.errName");
      if (!/^\S+@\S+\.\S+$/.test(form.email)) return t("quote.errEmail");
      if (!form.country_of_residence) return t("quote.errResidence");
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) return toast.error(err);
    setStep((s) => Math.min(TOTAL, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!form.ack1 || !form.ack2) return toast.error(t("quote.errAck"));
    const payload = {
      source: "r0am_website", brand: "R0AM by Wadiah Mutual", lead_type: "quote_request",
      customer_segment: form.segment, mobility_cycle: form.cycle,
      countries: [form.currentCountry, form.nextDestination, ...form.countries].filter(Boolean),
      traveler_count: form.travelerCount, plan_interest: form.plan,
      smartbag_interest: form.needs.includes("smartbag"),
      telemedicine_interest: form.needs.includes("telemedicine"),
      connectivity_interest: form.needs.includes("connectivity"),
      risk_options_interest: form.needs.includes("risk_options"),
      pre_existing_disclosure: form.needs.includes("pre_existing"),
      payment_preference: form.needs.includes("monthly_payment") ? "monthly" : "standard",
      contact: { name: form.name, email: form.email, whatsapp: form.whatsapp, country_of_residence: form.country_of_residence },
      notes: form.notes, preferred_start_date: form.start_date, compliance_acknowledged: form.ack1 && form.ack2,
    };
    const res = await submitLead("quotes", payload);
    if (!res.ok) return toast.error(t("quote.errAck"));
    setAssisted(payload.pre_existing_disclosure);
    setDone(true);
    window.scrollTo(0, 0);
  };

  const pct = Math.round((step / TOTAL) * 100);

  if (done) {
    const channel = form.whatsapp ? t("quote.onWhatsapp") : t("quote.byEmail");
    return (
      <div className="min-h-screen grid place-items-center px-6 pt-24 pb-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="surface p-10 max-w-lg text-center" data-testid="quote-success">
          <div className="w-16 h-16 rounded-full bg-health/15 grid place-items-center mx-auto"><CheckCircle2 size={32} className="text-health" /></div>
          <h1 className="mt-6 font-display font-bold text-3xl">{t("quote.successTitle")}</h1>
          <p className="mt-3 text-white/60 leading-relaxed">{t("quote.successBody", { name: form.name.split(" ")[0] || t("quote.traveler"), channel })}</p>
          {assisted && (
            <div className="mt-5 p-4 rounded-lg bg-gold/10 border border-gold/30 text-start" data-testid="assisted-review-notice">
              <p className="text-sm text-gold font-medium">{t("quote.assistedTitle")}</p>
              <p className="mt-1 text-xs text-white/55 leading-relaxed">{t("quote.assistedBody")}</p>
            </div>
          )}
          <p className="mt-5 text-xs text-white/35">{t("quote.demoNote")}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <CTAButton to="/" variant="secondary" testId="quote-home">{t("quote.backHome")}</CTAButton>
            <CTAButton to="/plans" variant="primary" testId="quote-plans">{t("quote.explore")}</CTAButton>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="quote-funnel">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between">
          <Link to="/"><Logo size="sm" showBy={false} testId="quote-logo" /></Link>
          <span className="font-mono text-xs text-white/40">{t("quote.step")} {step} / {TOTAL}</span>
        </div>

        <div className="mt-5 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <motion.div className="h-full bg-cta" animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} />
        </div>

        {(form.plan || form.cycle) && (
          <div className="mt-5 flex flex-wrap gap-2" data-testid="quote-preselect">
            {form.plan && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold/10 border border-gold/30 text-xs text-gold">{t("quote.planLabel")}: {t(`data.plans.${form.plan}.name`)}</span>}
            {form.cycle && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-signal/10 border border-signal/30 text-xs text-signal">{t(`data.cycles.${form.cycle}.label`)}</span>}
          </div>
        )}

        <div className="mt-10 surface p-6 sm:p-9">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
              {step === 1 && (
                <Step title={t("quote.s1Title")} hint={t("quote.s1Hint")}>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {SEGMENT_KEYS.map((s) => (
                      <Choice key={s} active={form.segment === s} onClick={() => set("segment", s)} testId={`segment-${s}`}>{t(`quote.segments.${s}`)}</Choice>
                    ))}
                  </div>
                </Step>
              )}

              {step === 2 && (
                <Step title={t("quote.s2Title")} hint={t("quote.s2Hint")}>
                  <Field label={t("quote.currentCountry")}><input className={inputCls} value={form.currentCountry} onChange={(e) => set("currentCountry", e.target.value)} placeholder={t("quote.currentPh")} data-testid="q-current-country" /></Field>
                  <Field label={t("quote.nextDestination")}><input className={inputCls} value={form.nextDestination} onChange={(e) => set("nextDestination", e.target.value)} placeholder={t("quote.nextPh")} data-testid="q-next-destination" /></Field>
                  <Field label={t("quote.countries12")}>
                    <div className="flex gap-2">
                      <input className={inputCls} value={countryInput} onChange={(e) => setCountryInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCountry())} placeholder={t("quote.countriesPh")} data-testid="q-country-input" />
                      <button onClick={addCountry} className="px-4 rounded-md bg-white/10 hover:bg-white/20 border border-white/10" data-testid="q-add-country"><Plus size={16} /></button>
                    </div>
                    {form.countries.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {form.countries.map((c) => (
                          <span key={c} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-navy-600 border border-white/10 text-sm" data-testid={`q-country-tag-${c}`}>
                            {c}
                            <button onClick={() => set("countries", form.countries.filter((x) => x !== c))}><X size={13} className="text-white/50 hover:text-white" /></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </Field>
                </Step>
              )}

              {step === 3 && (
                <Step title={t("quote.s3Title")}>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {TRAVELER_KEYS.map(([v, n]) => (
                      <Choice key={v} active={form.traveler === v} onClick={() => { set("traveler", v); set("travelerCount", n); }} testId={`traveler-${v}`}>{t(`quote.travelers.${v}`)}</Choice>
                    ))}
                  </div>
                  <Field label={t("quote.travelerCount")} className="mt-5"><input type="number" min="1" className={inputCls} value={form.travelerCount || ""} onChange={(e) => set("travelerCount", parseInt(e.target.value) || 0)} placeholder={t("quote.travelerCountPh")} data-testid="q-traveler-count" /></Field>
                </Step>
              )}

              {step === 4 && (
                <Step title={t("quote.s4Title")} hint={t("quote.s4Hint")}>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {NEED_KEYS.map((n) => (
                      <Choice key={n} active={form.needs.includes(n)} onClick={() => toggleNeed(n)} multi testId={`need-${n}`}>{t(`quote.needs.${n}`)}</Choice>
                    ))}
                  </div>
                </Step>
              )}

              {step === 5 && (
                <Step title={t("quote.s5Title")}>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label={t("quote.name")}><input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="q-name" /></Field>
                    <Field label={t("quote.email")}><input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="q-email" /></Field>
                    <Field label={t("quote.whatsapp")}><input className={inputCls} value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+..." data-testid="q-whatsapp" /></Field>
                    <Field label={t("quote.residence")}><input className={inputCls} value={form.country_of_residence} onChange={(e) => set("country_of_residence", e.target.value)} data-testid="q-residence" /></Field>
                    <Field label={t("quote.startDate")}><input type="date" className={inputCls} value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="q-start-date" /></Field>
                  </div>
                  <Field label={t("quote.notes")} className="mt-4"><textarea rows="3" className={inputCls} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder={t("quote.notesPh")} data-testid="q-notes" /></Field>
                </Step>
              )}

              {step === 6 && (
                <Step title={t("quote.s6Title")}>
                  <label className="flex gap-3 p-4 rounded-lg bg-white/[0.03] border border-white/10 cursor-pointer" data-testid="q-ack1">
                    <input type="checkbox" checked={form.ack1} onChange={(e) => set("ack1", e.target.checked)} className="mt-1 accent-cta" />
                    <span className="text-sm text-white/65 leading-relaxed">{t("quote.ack1")}</span>
                  </label>
                  <label className="flex gap-3 p-4 mt-3 rounded-lg bg-white/[0.03] border border-white/10 cursor-pointer" data-testid="q-ack2">
                    <input type="checkbox" checked={form.ack2} onChange={(e) => set("ack2", e.target.checked)} className="mt-1 accent-cta" />
                    <span className="text-sm text-white/65 leading-relaxed">{t("quote.ack2")}</span>
                  </label>
                </Step>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex items-center justify-between gap-3">
            {step > 1 ? (
              <button onClick={back} className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white" data-testid="quote-back"><ArrowLeft size={16} className="rtl:rotate-180" /> {t("common.back")}</button>
            ) : <span />}
            {step < TOTAL ? (
              <CTAButton onClick={next} variant="primary" testId="quote-next">{t("common.continue")} <ArrowRight size={16} className="rtl:rotate-180" /></CTAButton>
            ) : (
              <CTAButton onClick={submit} variant="primary" testId="quote-submit">{t("quote.submit")} <Check size={16} /></CTAButton>
            )}
          </div>
        </div>

        <p className="mt-5 text-center text-xs text-white/35">{t("quote.footnote", { pronounced: t("common.pronounced") })}</p>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-navy-900 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-signal transition-colors";

function Step({ title, hint, children }) {
  return (
    <div>
      <h2 className="font-display font-bold text-2xl sm:text-3xl">{title}</h2>
      {hint && <p className="mt-2 text-sm text-white/50">{hint}</p>}
      <div className="mt-7">{children}</div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={`mb-4 ${className}`}>
      <label className="block overline text-white/40 mb-2">{label}</label>
      {children}
    </div>
  );
}

function Choice({ active, onClick, children, multi, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`flex items-center justify-between gap-3 text-start p-4 rounded-lg border text-sm transition-all ${active ? "bg-navy-700 border-signal text-white" : "bg-white/[0.02] border-white/10 text-white/70 hover:border-white/25"}`}>
      {children}
      <span className={`shrink-0 w-5 h-5 grid place-items-center ${multi ? "rounded" : "rounded-full"} border ${active ? "bg-signal border-signal" : "border-white/25"}`}>
        {active && <Check size={13} className="text-white" />}
      </span>
    </button>
  );
}
